// Service Worker Registration Module
// This module handles registration and lifecycle management of the service worker
// which enables offline capabilities and faster subsequent page loads.

const isLocalhost = Boolean(
    window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' || // IPv6 localhost
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/) // IPv4 localhost
);

/**
 * Registers the service worker with enhanced error handling and lifecycle management
 * @param {Object} config - Configuration object for callbacks
 */
export function register(config) {
    if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
        try {
            const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
            
            if (publicUrl.origin !== window.location.origin) {
                console.warn('Service worker cannot be registered - origin mismatch');
                return;
            }

            window.addEventListener('load', async () => {
                const swUrl = `${process.env.PUBLIC_URL}/generate-sw.js`;
                
                if (isLocalhost) {
                    await checkValidServiceWorker(swUrl, config);
                    logLocalHostInfo();
                } else {
                    await registerValidSW(swUrl, config);
                }
            });
        } catch (error) {
            console.error('Service Worker registration failed:', error);
        }
    }
}

/**
 * Registers a valid service worker and handles its lifecycle
 * @param {string} swUrl - Service worker URL
 * @param {Object} config - Configuration object
 */
async function registerValidSW(swUrl, config) {
    try {
        const registration = await navigator.serviceWorker.register(swUrl);
        
        registration.onupdatefound = () => {
            const installingWorker = registration.installing;
            if (!installingWorker) return;

            installingWorker.onstatechange = () => {
                if (installingWorker.state !== 'installed') return;

                if (navigator.serviceWorker.controller) {
                    // New content available
                    console.log(
                        'New content is available. It will be used when all tabs are closed.'
                    );
                    config?.onUpdate?.(registration);
                } else {
                    // Content cached for offline use
                    console.log('Content is cached for offline use.');
                    config?.onSuccess?.(registration);
                }
            };
        };
    } catch (error) {
        console.error('Error during service worker registration:', error);
    }
}

/**
 * Validates the service worker and handles invalid cases
 * @param {string} swUrl - Service worker URL
 * @param {Object} config - Configuration object
 */
async function checkValidServiceWorker(swUrl, config) {
    try {
        const response = await fetch(swUrl, {
            headers: { 'Service-Worker': 'script' },
        });

        const contentType = response.headers.get('content-type');
        const isValidServiceWorker = response.status === 200 && 
                                   contentType?.includes('javascript');

        if (!isValidServiceWorker) {
            // Invalid service worker found
            const registration = await navigator.serviceWorker.ready;
            await registration.unregister();
            window.location.reload();
        } else {
            // Valid service worker found
            await registerValidSW(swUrl, config);
        }
    } catch (error) {
        console.log('App is running in offline mode - no internet connection');
    }
}

/**
 * Logs development-specific information for localhost
 */
function logLocalHostInfo() {
    navigator.serviceWorker.ready.then(() => {
        console.log(
            'This web app is being served cache-first by a service worker.\n' +
            'For more information, visit https://cra.link/PWA'
        );
    });
}

/**
 * Unregisters the service worker
 */
export async function unregister() {
    if ('serviceWorker' in navigator) {
        try {
            const registration = await navigator.serviceWorker.ready;
            await registration.unregister();
        } catch (error) {
            console.error('Error unregistering service worker:', error);
        }
    }
}